/**
 * CourseSessionService
 * Service for CourseSession entity operations
 * Route: api/course-sessions
 */
import { BaseService } from '../base/BaseService';
/**
 * CourseSessionService
 * Handles course session CRUD operations and ModelView endpoints
 */
export class CourseSessionService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CourseSessionService.DEFAULT_SERVICE_PATH,
            basePath: CourseSessionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ============================================================
    // Entity CRUD Operations
    // ============================================================
    /**
     * Get all course sessions
     * @returns Promise resolving to array of course sessions
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all course sessions (raw response)
     * @returns Promise resolving to ResponseData with course sessions
     */
    getAllRaw() {
        const url = this.buildUrl('');
        return this.http.get(url);
    }
    /**
     * Get course session by ID
     * @param id - Session ID
     * @returns Promise resolving to course session or null
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course session by ID (raw response)
     * @param id - Session ID
     * @returns Promise resolving to ResponseData with course session
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get sessions by course ID
     * @param courseId - Course ID
     * @returns Promise resolving to array of course sessions
     */
    async getByCourse(courseId) {
        const response = await this.getByCourseRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get sessions by course ID (raw response)
     * @param courseId - Course ID
     * @returns Promise resolving to ResponseData with course sessions
     */
    getByCourseRaw(courseId) {
        const endpoint = this.getEndpoint('getByCourse', `course/${courseId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new course session
     * @param session - Session data
     * @returns Promise resolving to created course session
     */
    async create(session) {
        const response = await this.createRaw(session);
        return response.data ?? null;
    }
    /**
     * Create a new course session (raw response)
     * @param session - Session data
     * @returns Promise resolving to ResponseData with created course session
     */
    createRaw(session) {
        const url = this.buildUrl('');
        return this.http.post(url, session);
    }
    /**
     * Update an existing course session
     * @param id - Session ID
     * @param session - Updated session data
     * @returns Promise resolving to updated course session
     */
    async update(id, session) {
        const response = await this.updateRaw(id, session);
        return response.data ?? null;
    }
    /**
     * Update an existing course session (raw response)
     * @param id - Session ID
     * @param session - Updated session data
     * @returns Promise resolving to ResponseData with updated course session
     */
    updateRaw(id, session) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, session);
    }
    /**
     * Mark course session as completed
     * @param id - Session ID
     * @returns Promise resolving to completed course session
     */
    async markCompleted(id) {
        const response = await this.markCompletedRaw(id);
        return response.data ?? null;
    }
    /**
     * Mark course session as completed (raw response)
     * @param id - Session ID
     * @returns Promise resolving to ResponseData with completed course session
     */
    markCompletedRaw(id) {
        const endpoint = this.getEndpoint('markCompleted', `${id}/complete`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    /**
     * Delete a course session
     * @param id - Session ID
     * @returns Promise resolving to true if successful
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a course session (raw response)
     * @param id - Session ID
     * @returns Promise resolving to ResponseData
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ============================================================
    // ModelView Endpoints
    // ============================================================
    /**
     * Get course session view by ID
     * @param id - Session ID
     * @returns Promise resolving to session model view or null
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course session view by ID (raw response)
     * @param id - Session ID
     * @returns Promise resolving to ResponseData with session model view
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('getView', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered session list view
     * @param filter - Optional filter criteria
     * @returns Promise resolving to array of session model views
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered session list view (raw response)
     * @param filter - Optional filter criteria
     * @returns Promise resolving to ResponseData with session model views
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || null);
    }
    /**
     * Get session views by course
     * @param courseId - Course ID
     * @returns Promise resolving to array of session model views
     */
    async getCourseViews(courseId) {
        const response = await this.getCourseViewsRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get session views by course (raw response)
     * @param courseId - Course ID
     * @returns Promise resolving to ResponseData with session model views
     */
    getCourseViewsRaw(courseId) {
        const endpoint = this.getEndpoint('getCourseViews', `course/${courseId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ============================================================
    // Visibility — audience management (Restricted sessions only)
    // ============================================================
    /**
     * Read the role/ministry/department audience grants for a Restricted session.
     * Empty lists == visible to all signed-in users.
     */
    async getAudience(sessionId) {
        const response = await this.getAudienceRaw(sessionId);
        return response.data ?? { roleNames: [], ministryIds: [], departmentIds: [] };
    }
    getAudienceRaw(sessionId) {
        return this.http.get(this.buildUrl(`${sessionId}/audience`));
    }
    /** Wholesale replace a session's audience grants. */
    async setAudience(sessionId, audience) {
        const response = await this.setAudienceRaw(sessionId, audience);
        return response.data ?? audience;
    }
    setAudienceRaw(sessionId, audience) {
        return this.http.put(this.buildUrl(`${sessionId}/audience`), audience);
    }
    // ============================================================
    // Streaming media (HLS, transcripts, audio): admin
    // ============================================================
    /** Media processing status for a session (status, error, transcript preview). */
    async getMedia(id) {
        const response = await this.getMediaRaw(id);
        return response.data ?? null;
    }
    getMediaRaw(id) {
        return this.http.get(this.buildUrl(`${id}/media`));
    }
    /** Queue (or with `force`, redo) HLS / transcript / audio processing for a session. */
    async processMedia(id, force = false) {
        const response = await this.processMediaRaw(id, force);
        return response.data ?? null;
    }
    processMediaRaw(id, force = false) {
        const body = { force };
        return this.http.post(this.buildUrl(`${id}/media/process`), body);
    }
}
CourseSessionService.DEFAULT_SERVICE_PATH = 'resources';
CourseSessionService.BASE_PATH = 'api/course-sessions';
//# sourceMappingURL=CourseSessionService.js.map